<template>
    <div class="custom-categories-type-1 no-actions">
        <div class="categories-list-container">
            <div class="list-actions" v-if="processedBuckets.length > 0">
                <button 
                    class="btn-link" 
                    @click="expandAll"
                    v-if="!isAllExpanded">
                    {{ $t('common.expand_all') }}
                </button>
                <button
                    class="btn-link"
                    @click="collapseAll"
                    v-if="!isAllCollapsed">
                    {{ $t('common.collapse_all') }}
                </button>
            </div>
            
            <ul class="categories-list">
                <template v-for="(item, index) in processedBuckets" :key="index">
                    <li
                        :class="{
                            'has-children': item.hasChildren,
                            'is-expanded': expandedItems.has(item.val),
                            ['level-' + item.level]: true
                        }"
                    >
                        
                        <div
                            class="category-name" 
                            @click="toggleExpand(item)"
                        >
                            <div class="value">{{ formatSbiCode(item.val) }}</div>
                            <div class="name">{{ item.displayName }}</div>
                        </div>
                        <div class="category-count">{{ formatNumber(item.count) }}</div>
                    </li>
                </template>
                <li v-if="processedBuckets.length === 0" class="no-data">
                    {{ $t('common.no_data_available') }}
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
    import { formatNumber } from '@/utils/formatting';
    import { formatSbiCode } from '@/utils/formatting';
    import { sbi_nl } from '@/utils/maps/sbi-nl';

    export default {
        name: 'FacetsSbi',
        props: {
            buckets: {
                type: Array,
                required: true
            },
            slug: {
                type: String,
                required: false
            }
        },
        data() {
            return {
                expandedItems: new Set(),
                sbiNlData: sbi_nl,
                sbiMap: {},
                sbiHierarchy: {}
            };
        },
        computed: {
            processedBuckets() {
                if (!this.buckets || this.buckets.length === 0) return [];
                return this.createHierarchicalBuckets();
            },
            allExpandableItems() {
                return new Set(
                    this.processedBuckets
                        .filter(item => item.hasChildren)
                        .map(item => item.val)
                );
            },
            isAllExpanded() {
                if (this.allExpandableItems.size === 0) return true;
                
                for (const value of this.allExpandableItems) {
                    if (!this.expandedItems.has(value)) {
                        return false;
                    }
                }
                return true;
            },
            isAllCollapsed() {
                return this.expandedItems.size === 0;
            }
        },
        created() {
            this.buildSbiMap(this.sbiNlData);
            this.buildHierarchy();
        },
        methods: {
            formatNumber,
            formatSbiCode,
            buildSbiMap(items, parentPath = []) {
                if (!items) return;
                
                items.forEach(item => {
                    const path = [...parentPath, item.value];
                    this.sbiMap[item.value] = {
                        text: item.text,
                        path: path,
                        parent: parentPath.length > 0 ? parentPath[parentPath.length - 1] : null,
                        children: item.children ? item.children.map(child => child.value) : []
                    };
                    
                    if (item.children && item.children.length > 0) {
                        this.buildSbiMap(item.children, path);
                    }
                });
            },
            buildHierarchy() {
                this.sbiHierarchy = {};
                
                Object.keys(this.sbiMap).forEach(sbiCode => {
                    const item = this.sbiMap[sbiCode];
                    
                    if (item.parent) {
                        if (!this.sbiHierarchy[item.parent]) {
                            this.sbiHierarchy[item.parent] = [];
                        }
                        this.sbiHierarchy[item.parent].push(sbiCode);
                    }
                });
            },
            getItemText(sbiCode) {
                return this.sbiMap[sbiCode]?.text || sbiCode;
            },
            getItemParent(sbiCode) {
                return this.sbiMap[sbiCode]?.parent || null;
            },
            getItemAncestors(sbiCode) {
                const ancestors = [];
                let current = this.getItemParent(sbiCode);
                
                while (current) {
                    ancestors.unshift(current);
                    current = this.getItemParent(current);
                }
                
                return ancestors;
            },
            createHierarchicalBuckets() {
                const result = [];
                const bucketsMap = {};
                const originalOrder = new Map();
                
                this.buckets.forEach((bucket, index) => {
                    originalOrder.set(bucket.val, index);
                    bucketsMap[bucket.val] = { ...bucket };
                });
                
                this.buckets.forEach(bucket => {
                    const ancestors = this.getItemAncestors(bucket.val);
                    
                    ancestors.forEach(ancestor => {
                        if (!bucketsMap[ancestor]) {
                            bucketsMap[ancestor] = {
                                val: ancestor,
                                count: 0,
                                isAggregated: true
                            };
                        }
                        
                        if (bucketsMap[ancestor].isAggregated) {
                            bucketsMap[ancestor].count += bucket.count;
                        }
                    });
                });
                
                const processLevel = (codes, level = 0, shown = true) => {
                    if (!codes || codes.length === 0) return;
                    
                    codes.forEach(code => {
                        if (!bucketsMap[code]) return;
                        
                        const item = bucketsMap[code];
                        const children = this.sbiHierarchy[code] || [];
                        const hasChildren = children.some(child => bucketsMap[child]);
                        
                        result.push({
                            ...item,
                            level,
                            displayName: this.getItemText(code),
                            hasChildren,
                            shown
                        });
                        
                        if (hasChildren && this.expandedItems.has(code)) {
                            const sortedChildren = [...children]
                                .filter(child => bucketsMap[child])
                                .sort((a, b) => {
                                    return String(a).localeCompare(String(b), undefined, { numeric: true });
                                });
                            
                            processLevel(sortedChildren, level + 1, shown);
                        }
                    });
                };
                
                const topLevelCodes = Object.keys(bucketsMap)
                    .filter(code => !this.getItemParent(code) || !bucketsMap[this.getItemParent(code)])
                    .sort((a, b) => {
                        const aInOriginal = originalOrder.has(a);
                        const bInOriginal = originalOrder.has(b);
                        
                        if (aInOriginal && bInOriginal) {
                            return originalOrder.get(a) - originalOrder.get(b);
                        }
                        
                        if (aInOriginal && !bInOriginal) return -1;
                        if (!aInOriginal && bInOriginal) return 1;
                        
                        return String(a).localeCompare(String(b), undefined, { numeric: true });
                    });
                
                processLevel(topLevelCodes);
                
                return result.filter(item => item.shown);
            },
            toggleExpand(item) {
                if (!item.hasChildren) return;
                
                if (this.expandedItems.has(item.val)) {
                    this.expandedItems.delete(item.val);
                } else {
                    this.expandedItems.add(item.val);
                }
                
                this.$forceUpdate();
            },
            expandAll() {
                this.processedBuckets.forEach(item => {
                    if (item.hasChildren) {
                        this.expandedItems.add(item.val);
                    }
                });
                
                this.$forceUpdate();
            },
            collapseAll() {
                this.expandedItems.clear();
                this.$forceUpdate();
            }
        }
    }
</script>